<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import logoUrl from 'assets/images/dashboard/pilot/logo.svg';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
  ssoAuthToken: {
    type: String,
    default: '',
  },
  ssoAccountId: {
    type: String,
    default: '',
  },
});

const router = useRouter();

// 如果有 SSO token，默认显示邮箱登录 tab，否则显示手机号登录 tab
const activeTab = ref(props.ssoAuthToken ? 'email' : 'phone');

// SSO 参数传递给子组件
const ssoParams = {
  email: props.email,
  ssoAuthToken: props.ssoAuthToken,
  ssoAccountId: props.ssoAccountId,
};

const handleLoginSuccess = () => {
  // 登录成功后跳转到 dashboard
  router.push('/');
};

const handleLoginError = (error) => {
  console.error('Login failed:', error);
};
</script>

<template>
  <div class="login-container">
    <!-- 左侧信息区域 -->
    <div class="login-info">
      <div class="info-content">
        <h1 class="info-title">{{ $t('LOGIN.TITLE') }}</h1>
        <p class="info-promo">{{ $t('LOGIN.PROMO') }}</p>
      </div>
    </div>

    <!-- 右侧登录表单区域 -->
    <div class="login-form-container">
      <div class="login-form-wrapper">
        <!-- Logo -->
        <div class="login-logo">
          <img :src="logoUrl" alt="Chatwoot" />
        </div>

        <!-- Tab切换 -->
        <div class="login-tabs">
          <button
            class="tab-button"
            :class="{ active: activeTab === 'phone' }"
            @click="activeTab = 'phone'"
          >
            {{ $t('LOGIN.TABS.PHONE') }}
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'email' }"
            @click="activeTab = 'email'"
          >
            {{ $t('LOGIN.TABS.EMAIL') }}
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'wechat' }"
            @click="activeTab = 'wechat'"
          >
            微信登录
          </button>
        </div>

        <!-- 登录表单 -->
        <div class="login-form-content">
          <PhoneLoginForm
            v-if="activeTab === 'phone'"
            @login-success="handleLoginSuccess"
            @login-error="handleLoginError"
          />
          <EmailLoginForm
            v-if="activeTab === 'email'"
            :email="ssoParams.email"
            :sso-auth-token="ssoParams.ssoAuthToken"
            :sso-account-id="ssoParams.ssoAccountId"
            @login-success="handleLoginSuccess"
            @login-error="handleLoginError"
          />
          <WechatLogin
            v-if="activeTab === 'wechat'"
            @login-success="handleLoginSuccess"
            @login-error="handleLoginError"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-container {
  display: flex;
  min-height: 100vh;
}

.login-info {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.info-content {
  color: white;
  max-width: 500px;
}

.info-title {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 20px;
}

.info-promo {
  font-size: 18px;
  opacity: 0.9;
}

.login-form-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  padding: 40px;
}

.login-form-wrapper {
  width: 100%;
  max-width: 450px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.login-logo {
  text-align: center;
  margin-bottom: 32px;
}

.login-logo img {
  height: 48px;
}

.login-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  border-bottom: 1px solid #e5e7eb;
}

.tab-button {
  flex: 1;
  padding: 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
}

.tab-button:hover {
  color: #374151;
}

.tab-button.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.login-form-content {
  min-height: 400px;
}

@media (max-width: 768px) {
  .login-info {
    display: none;
  }

  .login-form-container {
    flex: 1;
  }
}
</style>
